import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Building2,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Mail,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  Wand2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import logo from '../assets/brand/sissi-logo.svg';
import symbol from '../assets/brand/sissi-symbol.svg';
import { useAuth } from '../hooks/useAuth';
import { useSidePanel } from '../contexts/SidePanelContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Below this width the sidebar becomes a drawer. It is wider than the app's
// 768px "mobile" breakpoint on purpose: at tablet sizes the patient table and
// the detail panels need the 240px back more than the nav needs to be pinned.
export const SIDEBAR_BREAKPOINT = 1024;

const COLLAPSED_KEY = 'clinic-sidebar-collapsed';

interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
}

function initials(firstName?: string, lastName?: string, email?: string) {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (email) return email[0].toUpperCase();
  return 'U';
}

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const sidePanel = useSidePanel();
  const isCompact = useIsMobile(SIDEBAR_BREAKPOINT);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === 'true',
  );

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  // The clinic under the logo comes from the login response, so a session
  // opened before an admin renamed or reassigned the clinic would keep the old
  // one. Refreshed once per load; admins have no clinic to show.
  const isProfessional = user?.role === 'doctor' || user?.role === 'nutritionist';
  useEffect(() => {
    if (isProfessional) void refreshUser();
  }, [isProfessional, refreshUser]);

  // Close the drawer after navigating. Radix restores focus to the trigger and
  // handles the scroll lock, which the old hand-rolled drawer did by mutating
  // document.body.style directly.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const navItems: NavItem[] = [
    { id: 'dashboard', label: 'Inicio', path: '/app', icon: LayoutDashboard },
    { id: 'patients', label: 'Mis pacientes', path: '/app/patients', icon: Users },
    { id: 'insights', label: 'Insights', path: '/app/insights', icon: Lightbulb },
    { id: 'resources', label: 'Recursos', path: '/app/resources', icon: BookOpen },
    ...(user?.role === 'admin'
      ? [
          { id: 'clinics', label: 'Clínicas', path: '/app/clinics', icon: Building2 },
          { id: 'metrics', label: 'Métricas', path: '/app/metrics', icon: BarChart3 },
          { id: 'invites', label: 'Invitaciones', path: '/app/invites', icon: Mail },
        ]
      : []),
  ];

  const isActive = (path: string) =>
    path === '/app' ? location.pathname === '/app' : location.pathname.startsWith(path);

  // Only the docked sidebar collapses; in the drawer there is room for labels.
  const railed = collapsed && !isCompact;

  const nav = (
    <nav className="flex-1 overflow-y-auto p-3">
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {navItems.map(({ id, label, icon: Icon, path }) => {
          const active = isActive(path);
          const button = (
            <button
              onClick={() => navigate(path)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex h-(--control-height) w-full cursor-pointer items-center gap-3 rounded-control border-0 bg-transparent px-3 text-[15px] font-medium transition-colors',
                railed && 'justify-center px-0',
                active
                  ? 'bg-primary-subtle text-primary-subtle-foreground font-semibold'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Icon className="size-5 shrink-0" />
              {!railed && <span className="truncate">{label}</span>}
            </button>
          );

          return (
            <li key={id}>
              {railed ? (
                <Tooltip>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="right">{label}</TooltipContent>
                </Tooltip>
              ) : (
                button
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-1 border-t border-border pt-2">
        {(() => {
          const button = (
            <button
              onClick={() => sidePanel?.openAgent()}
              aria-label="Abrir el asistente"
              className={cn(
                'flex h-(--control-height) w-full cursor-pointer items-center gap-3 rounded-control border-0 bg-transparent px-3 text-[15px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                railed && 'justify-center px-0',
              )}
            >
              <Wand2 className="size-5 shrink-0" />
              {!railed && <span className="truncate">Asistente</span>}
            </button>
          );
          return railed ? (
            <Tooltip>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="right">Asistente</TooltipContent>
            </Tooltip>
          ) : (
            button
          );
        })()}
      </div>
    </nav>
  );

  const brand = (
    <div
      className={cn(
        'flex items-center gap-3 border-b border-border px-5 py-4',
        railed && 'justify-center px-0',
      )}
    >
      {/* The horizontal logo is the manual's default for the app. The rail is
       *  too narrow for it (the manual's minimum is 96px), so it shows the
       *  symbol alone, at 32px, above the 24px floor for the two-tone mark. */}
      {railed ? (
        <img src={symbol} alt="Sissi Health" className="size-8 shrink-0" />
      ) : (
        <div className="flex min-w-0 flex-col gap-1.5">
          <img src={logo} alt="Sissi Health" className="h-10 w-auto self-start" />
          {user?.role !== 'admin' && user?.clinic && (
            <span className="truncate text-xs text-muted-foreground">{user.clinic.name}</span>
          )}
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="border-t border-border p-3">
      <div
        className={cn(
          'mb-2 flex items-center gap-3 rounded-control bg-muted p-2.5',
          railed && 'justify-center bg-transparent p-0',
        )}
      >
        <Avatar className="size-9 shrink-0">
          <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
            {initials(user?.firstName, user?.lastName, user?.email)}
          </AvatarFallback>
        </Avatar>
        {!railed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </div>
            {user?.role && (
              <div className="text-xs text-muted-foreground capitalize">{user.role}</div>
            )}
          </div>
        )}
      </div>

      {railed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="w-full"
              onClick={() => {
                logout();
                navigate('/login');
              }}
              aria-label="Cerrar sesión"
            >
              <LogOut />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Cerrar sesión</TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            logout();
            navigate('/login');
          }}
        >
          <LogOut /> Cerrar sesión
        </Button>
      )}
    </div>
  );

  if (isCompact) {
    return (
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Abrir menú"
            className="fixed top-3 left-3 z-30 bg-card shadow-xs"
          >
            <Menu />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="flex w-[280px] flex-col gap-0 p-0">
          <SheetTitle className="sr-only">Navegación</SheetTitle>
          {brand}
          {nav}
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      aria-label="Navegación principal"
      className={cn(
        'sticky top-0 flex h-screen shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200',
        railed ? 'w-16' : 'w-60',
      )}
    >
      {brand}
      {nav}
      <div className="px-3 pb-1">
        <Button
          variant="ghost"
          size="sm"
          className={cn('w-full text-muted-foreground', railed && 'px-0')}
          onClick={() => setCollapsed((v) => !v)}
          aria-label={railed ? 'Expandir menú' : 'Plegar menú'}
        >
          {railed ? <PanelLeftOpen /> : <PanelLeftClose />}
          {!railed && 'Plegar'}
        </Button>
      </div>
      {footer}
    </aside>
  );
};

export default Sidebar;
